// 2026-07-20 - FormuLearning - 시험 배치 용출 프로파일 입력 그리드 (이슈 #42)
// 2026-08-03 수정 - 이슈 #52: 시점 행을 **오름차순**으로 세운다. 저장 배열이
//                   `10,15,30,45,60,0` 순인 데이터가 있어(입력 폼이 새 시점을 배열 끝에
//                   붙인다) 0분 행이 맨 아래에 놓였다. 그래프도 같은 정렬을 쓴다.
// 2026-08-31 수정 - 이슈 #58 S5 3차(#68): 정본 UI/UX v2.2 §5.4 로 재편.
//                   ① 용액마다 **`AVG(%)`·`SD` 2개 서브컬럼**(`M84-64`) — 종전에는 셀 하나뿐이라
//                      SD 를 화면에서 못 고쳤다(엑셀 업로드로만 들어왔다).
//                   ② 용액별 **`n`(반복 횟수)** 입력·뱃지(`M84-15`). 최소 3회는 **경고만**이며
//                      저장을 막지 않는다(사용자 확정 260831) — 실험 중간값을 적어 두는 경로를
//                      막을 이유가 없다.
//                   ③ **0 시점은 표에서 숨긴다**(`M84-16`). 그래프에는 그대로 나오고 f2 계산은
//                      종전대로 제외한다(ADR-0005). **저장 draft 에는 남겨 둔다** — 보이는 행만
//                      저장하면 0 시점이 소리 없이 사라진다.
//                   ④ 업로드 버튼을 입력창 **아래**에 둔다(`M84-14`·`27`) — 직접 입력이 주 경로다.
// 2026-08-31 수정 - 이슈 #58 S5 5차(#68, M84-75): **엑셀 업로드 진입점 폐지.** 3차에서
//                   입력창 아래로 내렸던 `↑ Upload (.xlsx)` 를 걷는다(`M84-14`·`27` 개정).
//                   붙여넣기(4차)가 서면서 엑셀 왕복이 주 경로가 아니게 됐다는 사용자 판단
//                   (260831). **아래로 내린 3차가 무의미해진 것이 아니라 중간 단계였다.**
//                   백엔드 파서는 남는다 — 화면 진입점만 걷는다.
// 2026-08-31 수정 - 이슈 #58 S5 4차(#68): **붙여넣기 grid**(`M84-72`, 정본 UI/UX v2.2 §5.9
//                   `COMP-PASTE-GRID`). 엑셀에서 범위를 복사해 아무 값 칸에 붙여넣으면
//                   앵커 칸부터 **행×열 그대로** 들어간다. 어댑터는 `lib/pasteGrid.ts` 이며
//                   의존성이 늘지 않는다(`Q-260813-22` 260831 승인).
// 2026-09-21 수정 - 이슈 #69(S6): f2 사유 문구를 `lib/dissolution.ts` 로 옮겼다(분석 구획과 공용).
// 2026-09-24 수정 - 이슈 #76(G5, M901-32): 미저장 편집 여부를 `onDirtyChange` 로 알린다 — 저장 전에는
//                   Next Optimization 을 잠근다. 빈 그리드 뼈대(`referenceProfiles`)는 호출부가 라운드 격자에서
//                   계산 제외를 뺀 것을 준다(M901-29 «입력칸에서도 뺀다»).
// 2026-11-03 수정 - 이슈 #114: viewer(조회 전용)는 값 입력·Save 비활성(«조회 전용 계정»).
// 파일 위치: frontend/src/components/DissolutionGrid.tsx
// 목적: init·opti 배치의 용액(pH)별·시점별 용출률을 화면에서 직접 입력·저장한다.
//       엑셀 업로드(Dissolution 시트)로 채워진 값도 같은 그리드에 나타나며 수정할 수 있다.
//       저장 즉시 백엔드가 f2를 재계산하므로(BR-S4-L09①) 결과를 그대로 표시한다.

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import type { ClipboardEvent } from 'react'
import type { DissolutionProfile, PhProfile } from '../api/step3'
import {
  applyMatrix, describePaste, dropIndexColumn, isRangePaste,
  parseClipboardMatrix, stripHeaderRows,
} from '../lib/pasteGrid'
import { F2_REASON_TEXT } from '../lib/dissolution'
import { useCanWrite, READ_ONLY_TITLE } from '../hooks/useCanWrite'

interface Props {
  batchLabel: string
  profiles: DissolutionProfile[]
  /** 대조약(reference) 프로파일 — 비어 있는 그리드의 pH·시점 뼈대로 쓴다(Lot 격자). */
  referenceProfiles: PhProfile[]
  f2: number | null
  f2BySolution: Record<string, number>
  f2Reason: string | null
  onSave: (profiles: DissolutionProfile[]) => Promise<void>
  /** 미저장 편집 여부 알림(#76, M901-32). 언마운트 시 false 로 한 번 더 알린다. */
  onDirtyChange?: (dirty: boolean) => void
}


/** `n` 최소 기준(`M84-15`). 미달이면 경고만 하고 저장은 막지 않는다(사용자 확정 260831). */
const MIN_N = 3

export default function DissolutionGrid({
  batchLabel, profiles, referenceProfiles, f2, f2BySolution, f2Reason, onSave, onDirtyChange,
}: Props) {
  // 그리드 뼈대: 저장값이 있으면 그것을, 없으면 대조약 격자를 0으로 깔아 준다.
  // 어느 쪽이든 시점 오름차순으로 세운다(#52) — 저장 배열 순서에 표가 좌우되지 않게.
  const seeded = useMemo<DissolutionProfile[]>(() => {
    const src: DissolutionProfile[] = profiles.length > 0
      ? profiles
      : referenceProfiles.map((p) => ({
          ph: p.ph,
          data: (p.time_points || []).map((tp) => ({ time: tp.time, dissolution: 0, sd: 0 })),
        }))
    return src.map((p) => ({ ...p, data: [...(p.data || [])].sort((a, b) => a.time - b.time) }))
  }, [profiles, referenceProfiles])

  const [draft, setDraft] = useState<DissolutionProfile[]>(seeded)
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const canWrite = useCanWrite()

  // 미저장 편집 알림 — 콜백은 ref 로 든다. 호출부가 매 렌더 새 함수를 넘겨도 알림이 되풀이되지 않는다.
  const dirtyCb = useRef(onDirtyChange)
  useEffect(() => { dirtyCb.current = onDirtyChange })
  useEffect(() => { dirtyCb.current?.(dirty) }, [dirty])
  useEffect(() => () => { dirtyCb.current?.(false) }, [])

  // 저장·업로드로 서버 값이 바뀌면(미편집 상태일 때만) 그리드를 따라 갱신한다.
  const [synced, setSynced] = useState(seeded)
  if (!dirty && synced !== seeded) {
    setSynced(seeded)
    setDraft(seeded)
  }

  // 표에 세울 행 — **0 시점은 뺀다**(`M84-16`). 원 배열 인덱스를 같이 들고 다녀야
  // 편집이 draft 의 올바른 행에 꽂힌다. 숨긴 0 행은 draft 에 그대로 남아 함께 저장된다.
  //
  // **전 행이 0인 격자는 숨기지 않는다.** 그런 데이터는 0분 측정이 아니라 시점이 아직
  // 안 잡힌 **빈 골격**이다(구 `+ Time Point` 가 새 행을 0으로 붙이던 자리, 260831 실측).
  // 그것까지 감추면 표가 통째로 비어 손댈 길이 없어진다.
  const rows = useMemo(() => {
    const data = draft[0]?.data ?? []
    const indexed = data.map((d, idx) => ({ time: d.time, idx }))
    const nonZero = indexed.filter((r) => r.time !== 0)
    return nonZero.length > 0 ? nonZero : indexed
  }, [draft])
  const hiddenZero = rows.length < (draft[0]?.data ?? []).length

  // 2026-07-23 - 이슈 #50 S-3: 시점(time)은 Lot 대조약 격자가 정본(BR-LOT-09·10)이므로
  // 배치에서 수정하지 않는다 — 측정값(AVG·SD)만 편집한다. (+ Time point도 폐기)
  const setCell = (phIdx: number, tIdx: number, field: 'dissolution' | 'sd', value: number) => {
    setDirty(true)
    setDraft((prev) =>
      prev.map((p, pi) =>
        pi === phIdx
          ? { ...p, data: p.data.map((d, di) => (di === tIdx ? { ...d, [field]: value } : d)) }
          : p,
      ),
    )
  }

  /** 용액별 반복 횟수(`M84-15`). 빈 칸은 `null` — 0회가 아니라 "미입력"이다. */
  const setN = (phIdx: number, raw: string) => {
    const v = raw.trim() === '' ? null : Number(raw)
    setDirty(true)
    setDraft((prev) => prev.map((p, pi) => (pi === phIdx ? { ...p, n: v } : p)))
  }

  // ── 붙여넣기(`M84-72`) ─────────────────────────────────────────────
  // 값 칸의 좌표계: 행 = **표에 보이는 행 서수**(숨긴 0 시점은 세지 않는다),
  // 열 = `용액 인덱스 × 2 + (SD 면 1)`. 열 총수는 `draft.length * 2` 다.
  const colCount = draft.length * 2
  const [pasteNote, setPasteNote] = useState<string | null>(null)

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, rowOrd: number, colIdx: number) => {
    const text = e.clipboardData.getData('text/plain')
    // 단일 셀은 가로채지 않는다 — 평범한 한 칸 붙여넣기의 기본 동작을 남긴다.
    if (!isRangePaste(text)) return
    e.preventDefault()

    let matrix = stripHeaderRows(parseClipboardMatrix(text))
    // 시점 열까지 함께 복사해 오는 일이 흔하다. 값이 **정확히 일치할 때만** 그 열을 버린다.
    if (colIdx === 0) {
      matrix = dropIndexColumn(matrix, rows.slice(rowOrd).map((r) => r.time))
    }

    // 셀마다 `setCell` 을 부르면 stale closure 로 마지막 칸만 남는다. 복사본에 모두
    // 채운 뒤 **한 번**에 넣는다. 숨긴 0 행은 `rows[i].idx` 매핑을 거치므로 닿지 않는다.
    const next = draft.map((p) => ({ ...p, data: p.data.map((d) => ({ ...d })) }))
    const res = applyMatrix({
      matrix, anchorRow: rowOrd, anchorCol: colIdx,
      rowCount: rows.length, colCount,
      write: (r, c, value) => {
        const target = next[Math.floor(c / 2)]?.data[rows[r].idx]
        if (!target) return
        if (c % 2 === 0) target.dissolution = value
        else target.sd = value
      },
    })

    if (res.filled > 0) {
      setDraft(next)
      setDirty(true)
    }
    setPasteNote(describePaste(res))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(draft)
      setDirty(false)
      setPasteNote(null)
    } finally {
      setSaving(false)
    }
  }

  if (draft.length === 0) {
    return (
      <div className="rounded border p-2 text-[10px] text-text-secondary" style={{ borderColor: 'var(--border-subtle)' }}>
        {batchLabel} — enter the reference drug dissolution profile first to define pH and time points.
      </div>
    )
  }

  const lowN = draft.filter((p) => p.n != null && p.n < MIN_N)

  return (
    <div
      className="rounded border p-2"
      style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--color-bg-panel)' }}
      data-testid="dissolution-grid"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-baseline gap-1.5">
          <p className="text-[10px] font-semibold text-text-body">{batchLabel}</p>
          {/* M84-72 — 붙여넣기가 주 입력 경로임을 표에서 알린다. */}
          <span
            className="text-[9px] text-text-muted"
            title="엑셀에서 범위를 복사해 값 칸 아무 곳에나 붙여넣으면 그 칸부터 행×열 그대로 채워집니다. 시점 열을 함께 복사해도 자동으로 맞춰지고, 빈 칸과 숫자가 아닌 칸은 건너뜁니다."
            data-testid="paste-hint"
          >
            ⌘/Ctrl+V 붙여넣기 가능
          </span>
        </div>
        <div className="flex items-center gap-2">
          {f2 != null ? (
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-semibold"
              style={{ background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' }}
              title={Object.entries(f2BySolution).map(([ph, v]) => `pH ${ph}: ${v.toFixed(1)}`).join('\n')}
            >
              f2 = {f2.toFixed(1)}
            </span>
          ) : (
            <span className="text-[9px] text-text-secondary" title={f2Reason ? F2_REASON_TEXT[f2Reason] ?? f2Reason : undefined}>
              f2 = —
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !dirty || !canWrite}
            title={canWrite ? undefined : READ_ONLY_TITLE}
            className="rounded px-2 py-0.5 text-[10px] font-medium text-accent-teal transition-colors hover:bg-bg-input disabled:opacity-40"
            style={{ border: '1px solid var(--border-teal)' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* 2026-07-23 - 열을 균등 배분(w-full)하고 값 입력을 용액명 헤더와 가운데 정렬(사용자 피드백). */}
      <table className="w-full table-fixed text-[10px]" data-testid="dissolution-table">
        <thead>
          <tr>
            <th className="w-14 px-1 py-0.5 text-center font-medium text-text-secondary" rowSpan={2}>min</th>
            {draft.map((p, pi) => (
              <th key={pi} colSpan={2} className="px-1 py-0.5 text-center font-medium text-text-secondary">
                <span className="inline-flex items-center justify-center gap-1">
                  <span>pH {p.ph}</span>
                  {/* n 뱃지 겸 입력(M84-15). 최소 3회 미달은 경고색이되 저장은 열려 있다. */}
                  <span
                    className="inline-flex items-center gap-0.5 rounded px-1 text-[9px]"
                    style={p.n != null && p.n < MIN_N
                      ? { color: 'var(--color-warn)', border: '1px solid var(--color-warn)' }
                      : { color: 'var(--color-text-secondary)', border: '1px solid var(--border-subtle)' }}
                    title={`반복 횟수(n) — 최소 ${MIN_N}회 권장. 그래프 범례에 pH ${p.ph} (n=…)로 표기됩니다`}
                  >
                    n=
                    <input
                      disabled={!canWrite}
                      type="number"
                      min={1}
                      value={p.n ?? ''}
                      onChange={(e) => setN(pi, e.target.value)}
                      className="w-7 bg-transparent text-center text-[9px] outline-none"
                      style={{ color: 'inherit' }}
                      data-testid={`n-input-${p.ph}`}
                      placeholder="—"
                    />
                  </span>
                </span>
              </th>
            ))}
          </tr>
          {/* 서브컬럼(M84-64) — 대조약 표와 같은 형태로 AVG 왼쪽·SD 오른쪽. */}
          <tr>
            {draft.map((_, pi) => (
              <Fragment key={pi}>
                <th className="px-1 pb-0.5 text-center text-[9px] font-normal text-text-muted">AVG(%)</th>
                <th className="px-1 pb-0.5 text-center text-[9px] font-normal text-text-muted">SD</th>
              </Fragment>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, rowOrd) => (
            <tr key={r.idx}>
              {/* 시점은 읽기 전용 — 격자 정본은 Lot 대조약(BR-LOT-09·10, 이슈 #50 S-3) */}
              <td className="px-2 py-0.5 text-center text-[10px] text-text-secondary">{r.time}</td>
              {draft.map((p, pi) => (
                <Fragment key={pi}>
                  <td className="px-1 py-0.5 text-center">
                    <input
                      disabled={!canWrite}
                      type="number"
                      value={p.data[r.idx]?.dissolution ?? 0}
                      onChange={(e) => setCell(pi, r.idx, 'dissolution', Number(e.target.value))}
                      onPaste={(e) => handlePaste(e, rowOrd, pi * 2)}
                      className="w-full max-w-[90px] rounded bg-bg-input px-1 py-0.5 text-center text-[10px] text-text-body"
                      style={{ border: '1px solid var(--border-subtle)' }}
                      data-testid={`avg-${p.ph}-${r.time}`}
                    />
                  </td>
                  <td className="px-1 py-0.5 text-center">
                    <input
                      disabled={!canWrite}
                      type="number"
                      step={0.1}
                      value={p.data[r.idx]?.sd ?? 0}
                      onChange={(e) => setCell(pi, r.idx, 'sd', Number(e.target.value))}
                      onPaste={(e) => handlePaste(e, rowOrd, pi * 2 + 1)}
                      className="w-full max-w-[70px] rounded bg-bg-input px-1 py-0.5 text-center text-[10px] text-text-body"
                      style={{ border: '1px solid var(--border-subtle)' }}
                      data-testid={`sd-${p.ph}-${r.time}`}
                    />
                  </td>
                </Fragment>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* 입력 경로는 직접 입력·붙여넣기뿐이다 — 엑셀 업로드 진입점은 폐지됐다(M84-75). */}
      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        {pasteNote && (
          <span className="text-[9px] text-accent-teal" data-testid="paste-note">
            {pasteNote}
          </span>
        )}
        {hiddenZero && (
          <span className="text-[9px] text-text-muted" data-testid="zero-note">
            0분 시점은 입력이 불요해 표에서 감췄습니다 — 그래프에는 그대로 나오고 f2 계산에서는 제외됩니다.
          </span>
        )}
        {lowN.length > 0 && (
          <span className="text-[9px] text-warn" data-testid="low-n-warn">
            ⚠ 반복 횟수가 {MIN_N}회 미만인 용액이 있습니다 (
            {lowN.map((p) => `pH ${p.ph} n=${p.n}`).join(', ')}) — 저장은 됩니다.
          </span>
        )}
      </div>
    </div>
  )
}
